import { ChamferOutline } from "@/components/chamfer";
import { DashboardKpiCardHeader } from "@/components/dashboard/dashboard-stats/DashboardKpiCardHeader";
import { DashboardStafferTrendChart } from "@/components/dashboard/dashboard-stats/trend-and-funnel-charts/DashboardStafferTrendChart";
import { PaceProjectionChart } from "@/components/dashboard/dashboard-stats/trend-and-funnel-charts/PaceProjectionChart";
import { Card } from "@ui/card";
import { Skeleton } from "@ui/skeleton";

import { DASHBOARD_STAT_CARD_HEIGHT_CLASS } from "@/lib/constants";
import { dashboardKpis } from "@/lib/dashboardKpis";
import { formatting } from "@/lib/formatting";
import { cn } from "@/lib/utils";
import {
  DashboardComparisonMetric,
  DashboardKpiOverview,
  GoalPeriod,
  PerformanceTier,
  StafferStatKind,
} from "@/types";

// Card padding-top + the h-10 header + its mb-1. The chart area starts below
// this, so a full-height line has to reach back up over it.
const HEADER_BLOCK_PX = 58;

// A projected total this close to goal still reads as on pace, not behind.
const ALMOST_GOAL_RATIO = 0.85;

const METRIC_COPY: Record<
  DashboardComparisonMetric,
  { accentClassName: string; eyebrow: string; title: string }
> = {
  [DashboardComparisonMetric.TIME_TO_SCREEN]: {
    accentClassName: "border-t-lever-border-error",
    eyebrow: "TTFS · Trend",
    title: "Time to first screen",
  },
  [DashboardComparisonMetric.SUBMITS]: {
    accentClassName: "border-t-lever-border-information",
    eyebrow: "Submits · Trend",
    title: "Submits projection",
  },
  [DashboardComparisonMetric.REVENUE]: {
    accentClassName: "border-t-lever-border-success",
    eyebrow: "Revenue · Trend",
    title: "Revenue projection",
  },
};

export const DashboardTeamTrendsCard = ({
  goalsPeriod,
  isPending,
  metric,
  onMetricChange,
  overview,
}: {
  goalsPeriod: GoalPeriod;
  isPending: boolean;
  metric: DashboardComparisonMetric;
  onMetricChange: (metric: DashboardComparisonMetric) => void;
  overview: DashboardKpiOverview | undefined;
}) => {
  /* Helpers */
  const isTimeToScreen = metric === DashboardComparisonMetric.TIME_TO_SCREEN;
  const isSubmits = metric === DashboardComparisonMetric.SUBMITS;
  const copy = METRIC_COPY[metric];

  const goal = dashboardKpis.goalFor({
    metric,
    overview,
    perStaffer: false,
    period: goalsPeriod,
  });
  const periodData = dashboardKpis.periodFor(overview, goalsPeriod);
  const progress = periodData
    ? dashboardKpis.elapsedFraction(
        periodData.periodStart,
        periodData.periodEnd,
      )
    : 1;
  const current = (() => {
    if (!periodData) return 0;
    return isSubmits ? periodData.totals.submits : periodData.totals.revenue;
  })();
  const projected = Math.round(current / progress);
  const projectedRatio = goal > 0 ? projected / goal : 0;
  const projectionTier = (() => {
    if (projectedRatio >= 1) return PerformanceTier.GREEN;
    if (projectedRatio >= ALMOST_GOAL_RATIO) return PerformanceTier.ORANGE;
    return PerformanceTier.RED;
  })();

  const formatValue = (value: number) =>
    isSubmits ? value.toLocaleString() : formatting.compactUsd(value);

  /* Handlers */
  const handleNextMetric = () => {
    onMetricChange(dashboardKpis.nextMetric(metric));
  };

  /* Render */
  const renderContent = () => {
    if (isPending) return <Skeleton className="min-h-0 flex-1" />;
    if (isTimeToScreen) {
      return (
        <DashboardStafferTrendChart
          chartTopPercent={dashboardKpis.cardTopPercent(HEADER_BLOCK_PX)}
          kind={StafferStatKind.TIME_TO_SCREEN}
          rows={overview ? overview.trend : []}
          valueForRow={(row) => row.medianHoursToFirstScreen}
          onOpenScreenBreakdown={() => undefined}
        />
      );
    }

    return (
      <PaceProjectionChart
        current={current}
        formatValue={formatValue}
        goal={goal}
        progress={progress}
        projected={projected}
        tier={projectionTier}
      />
    );
  };

  const render = () => (
    <Card
      key={metric}
      className={cn(
        "lever-analysis-card flex min-h-0 w-full min-w-0 flex-col overflow-hidden !rounded-[var(--lever-radius-md)] border-lever-border-light bg-lever-surface-default px-4 pb-0 pt-3.5 font-lever-suisse shadow-none",
        DASHBOARD_STAT_CARD_HEIGHT_CLASS,
      )}
    >
      <ChamferOutline />
      <DashboardKpiCardHeader
        className="mb-1"
        eyebrow={copy.eyebrow}
        onNext={handleNextMetric}
        title={copy.title}
      />
      {renderContent()}
    </Card>
  );

  return render();
};
